import { useState } from "react"
import { makeStyles } from "tss-react/mui"
import { ArrowDropDown } from "@mui/icons-material"
import { Menu, MenuItem, Tooltip, Typography } from "@mui/material"
import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChartGantt, faChartBar, faSearch } from "@fortawesome/pro-solid-svg-icons"
import { LocalizedEnum, LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { StandardButton } from "../../../../buttons/StandardButton"

const useStyles = makeStyles()((theme) => ({
    menuButton: {
        color: theme.palette.tailwind.slate[800],
        padding: ".5rem 1rem",
        flex: "none",
        background: theme.palette.common.white,
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        borderRadius: "0.75rem",
        textTransform: "none",
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        "&:hover": {
            background: theme.palette.grey[100],
        },
    },
    menuItem: {
        display: "grid",
        gridTemplateColumns: "1rem 1fr",
        gap: ".5rem",
        alignItems: "center",
    },
    disabledItem: {
        pointerEvents: "none",
    },
}))

export enum ViewType {
    TIMELINE = "TIMELINE",
    AVAILABLE_TIME = "AVAILABLE_TIME",
    ROOM_FINDER = "ROOM_FINDER",
}

const VIEWS: Record<ViewType, { icon: IconProp }> = {
    [ViewType.TIMELINE]: { icon: faChartBar },
    [ViewType.AVAILABLE_TIME]: { icon: faChartGantt },
    [ViewType.ROOM_FINDER]: { icon: faSearch },
}

export function BookingViewMenu({
    selectedView,
    onChangeView,
}: {
    selectedView: ViewType
    onChangeView: (view: ViewType) => void
}) {
    const { classes, cx } = useStyles()

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const handleSelectView = (view: ViewType) => {
        onChangeView(view)
        handleClose()
    }

    return (
        <>
            <LocalizedStrict id="create-order-tooltip-view-button" attrs={{ title: true }}>
                <Tooltip title="Select booking view">
                    <StandardButton
                        color="grey"
                        variant="outlined"
                        size="medium"
                        fullWidth={false}
                        updateDefaultClasses={classes.menuButton}
                        onClick={handleClick}
                        startIcon={<FontAwesomeIcon icon={VIEWS[selectedView].icon} />}
                        endIcon={<ArrowDropDown />}
                    >
                        <LocalizedEnum base="create-order-view" enumValue={selectedView}>
                            <> {selectedView}</>
                        </LocalizedEnum>
                    </StandardButton>
                </Tooltip>
            </LocalizedStrict>

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                {Object.values(ViewType)
                    .filter((view) => view !== selectedView)
                    .map((view) => (
                        <MenuItem
                            key={view}
                            onClick={() => handleSelectView(view)}
                            disabled={view === ViewType.ROOM_FINDER}
                            className={cx(classes.menuItem, view === ViewType.ROOM_FINDER && classes.disabledItem)}
                        >
                            <FontAwesomeIcon icon={VIEWS[view].icon} />
                            <LocalizedEnum base="create-order-view" enumValue={view}>
                                <Typography variant="body1">{view}</Typography>
                            </LocalizedEnum>
                        </MenuItem>
                    ))}
            </Menu>
        </>
    )
}
